import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Check, ChevronDown, Loader2, Search, SlidersHorizontal, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useIsMobile } from '@/shared/hooks/useMediaQuery'
import { DatePicker } from '@/components/erp/date-picker'

/**
 * Filter controls, built at PollSphere's control size (`h-9`, `text-erp-sm`,
 * 6px radius) from its own token set. PollSphere filtered with a plain chip row,
 * so this stays a horizontal row — never a 280px filter sidebar eating grid width.
 *
 * Below 768px the row wraps and each control goes full width; the row scrolls
 * rather than the page.
 */

export function FilterBar({
  children, right, activeCount = 0,
}: { children: ReactNode; right?: ReactNode; activeCount?: number }) {
  const isMobile = useIsMobile()
  // Six stacked controls push the grid off a phone screen entirely. Collapsed by
  // default there, expanded whenever a filter is already applied so an active
  // filter is never invisible. Desktop is unaffected.
  const [open, setOpen] = useState(false)
  const expanded = !isMobile || open || activeCount > 0

  return (
    <div className="erp-card px-3 py-2.5 flex flex-wrap items-center gap-2">
      {isMobile && (
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={expanded}
          className={cn(
            'h-9 px-3 inline-flex items-center gap-2 border rounded-md text-erp-sm w-full',
            activeCount > 0
              ? 'border-chocolate/50 bg-chocolate-soft/40 text-chocolate font-medium'
              : 'border-input bg-background',
          )}
        >
          <SlidersHorizontal className="h-3.5 w-3.5" />
          Filters
          {activeCount > 0 && <span className="erp-metric">({activeCount})</span>}
          <ChevronDown className={cn('h-3.5 w-3.5 ml-auto transition-transform', expanded && 'rotate-180')} />
        </button>
      )}
      {expanded && children}
      {right && <div className="ml-auto flex items-center gap-2 axis-full-sm">{right}</div>}
    </div>
  )
}

export function SearchInput({
  value, onChange, placeholder = 'Search…', className,
}: {
  value: string
  onChange: (v: string) => void
  placeholder?: string
  className?: string
}) {
  return (
    <div className={cn(
      'flex items-center gap-2 px-3 h-9 border border-input rounded-md bg-background',
      'min-w-0 flex-1 sm:flex-none sm:w-64 axis-full-sm',
      className,
    )}>
      <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="bg-transparent outline-none text-erp-sm w-full min-w-0"
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label="Clear search"
          className="shrink-0 text-muted-foreground hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  )
}

export interface Option {
  value: string
  label: string
  /** Rendered right-aligned and dimmed — facet counts from the API. */
  count?: number
}

/**
 * Multi-select for the repeatable query params (`?status=A&status=B`).
 *
 * Closes on outside click and on Escape. Selection is applied immediately rather
 * than on an Apply button: every one of these maps to a single query param, so
 * there is nothing to batch.
 */
export function MultiSelect({
  label, options, selected, onChange, className, emptyLabel = 'All',
}: {
  label: string
  options: Option[]
  selected: string[]
  onChange: (next: string[]) => void
  className?: string
  emptyLabel?: string
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDown)
    window.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      window.removeEventListener('keydown', onKey)
    }
  }, [open])

  const toggle = (value: string) => {
    onChange(selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value])
  }

  const summary = selected.length === 0
    ? emptyLabel
    : selected.length === 1
      ? (options.find((o) => o.value === selected[0])?.label ?? selected[0])
      : `${selected.length} selected`

  return (
    <div ref={ref} className={cn('relative axis-full-sm', className)}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className={cn(
          'h-9 px-3 inline-flex items-center gap-2 border rounded-md bg-background text-erp-sm w-full',
          selected.length > 0
            ? 'border-chocolate/50 text-chocolate font-medium'
            : 'border-input text-foreground',
        )}
      >
        <span className="text-muted-foreground">{label}</span>
        <span className="truncate max-w-[10rem]">{summary}</span>
        <ChevronDown className="h-3.5 w-3.5 opacity-60 ml-auto shrink-0" />
      </button>

      {open && (
        <div className="absolute z-30 mt-1 min-w-full w-max max-w-[min(20rem,90vw)] max-h-72 overflow-y-auto erp-card shadow-lg py-1">
          {options.length === 0 && (
            <div className="px-3 py-2 text-erp-xs text-muted-foreground">No values yet</div>
          )}
          {options.map((o) => {
            const on = selected.includes(o.value)
            return (
              <button
                key={o.value}
                type="button"
                onClick={() => toggle(o.value)}
                className="w-full px-3 py-1.5 flex items-center gap-2 text-erp-sm hover:bg-muted text-left"
              >
                <span className={cn(
                  'h-3.5 w-3.5 shrink-0 border rounded-[3px] inline-flex items-center justify-center',
                  on ? 'bg-chocolate border-chocolate text-chocolate-foreground' : 'border-input',
                )}>
                  {on && <Check className="h-2.5 w-2.5" />}
                </span>
                <span className="truncate flex-1">{o.label}</span>
                {o.count !== undefined && (
                  <span className="text-erp-xs text-muted-foreground erp-metric">{o.count}</span>
                )}
              </button>
            )
          })}
          {selected.length > 0 && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="w-full mt-1 px-3 py-1.5 text-erp-xs text-muted-foreground hover:text-foreground border-t border-border text-left"
            >
              Clear {label.toLowerCase()}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * A multi-select whose options cannot be preloaded.
 *
 * `MultiSelect` above takes a fixed option list, which is right for fifteen statuses
 * and wrong for forty thousand polling stations: the list cannot be shipped, so the
 * options have to be searched.
 *
 * Two consequences shape it:
 *
 * - **Selected items keep their labels independently of the search results.** Once a
 *   station is chosen it stays legible even though the next query no longer returns it.
 *   Holding only ids would make the control forget what it is filtering by the moment
 *   the search box is retyped.
 * - **The search is debounced and asks for a handful.** It runs against a real endpoint
 *   over a field connection, so it waits for a pause and takes the first few matches
 *   rather than paging.
 */
export function AsyncMultiSelect({
  label, selected, onChange, search, placeholder = 'Type to search', className,
  emptyLabel = 'All', minChars = 2,
}: {
  label: string
  /** Value AND label, because the caller cannot resolve an id back to a name. */
  selected: { value: string; label: string }[]
  onChange: (next: { value: string; label: string }[]) => void
  search: (term: string) => Promise<{ value: string; label: string; sub?: string }[]>
  placeholder?: string
  className?: string
  emptyLabel?: string
  minChars?: number
}) {
  const [open, setOpen] = useState(false)
  const [term, setTerm] = useState('')
  const [results, setResults] = useState<{ value: string; label: string; sub?: string }[]>([])
  const [loading, setLoading] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const seq = useRef(0)

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDown)
    window.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      window.removeEventListener('keydown', onKey)
    }
  }, [open])

  useEffect(() => {
    if (term.trim().length < minChars) { setResults([]); return }
    const mine = ++seq.current
    setLoading(true)
    const handle = setTimeout(() => {
      void search(term.trim())
        .then((r) => { if (seq.current === mine) setResults(r) })
        .catch(() => { if (seq.current === mine) setResults([]) })
        .finally(() => { if (seq.current === mine) setLoading(false) })
    }, 300)
    return () => { clearTimeout(handle); setLoading(false) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term, minChars])

  const chosen = new Set(selected.map((s) => s.value))
  const toggle = (o: { value: string; label: string }) => {
    onChange(chosen.has(o.value)
      ? selected.filter((s) => s.value !== o.value)
      : [...selected, { value: o.value, label: o.label }])
  }

  const summary = selected.length === 0
    ? emptyLabel
    : selected.length === 1 ? selected[0]!.label : `${selected.length} selected`

  return (
    <div ref={ref} className={cn('relative axis-full-sm', className)} data-multiselect={label}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className={cn(
          'h-9 px-3 inline-flex items-center gap-2 border rounded-md bg-background text-erp-sm w-full',
          selected.length > 0
            ? 'border-chocolate/50 text-chocolate font-medium'
            : 'border-input text-foreground',
        )}
      >
        <span className="text-muted-foreground">{label}</span>
        <span className="truncate max-w-[12rem]">{summary}</span>
        <ChevronDown className="h-3.5 w-3.5 opacity-60 ml-auto shrink-0" />
      </button>

      {open && (
        <div className="absolute z-30 mt-1 min-w-full w-max max-w-[min(24rem,90vw)] erp-card shadow-lg py-1">
          <div className="px-2 pb-1">
            <div className="flex items-center gap-2 px-2 h-8 border border-input rounded-md bg-background">
              <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <input
                autoFocus
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                placeholder={placeholder}
                aria-label={`Search ${label.toLowerCase()}`}
                className="bg-transparent outline-none text-erp-sm w-full min-w-0"
              />
              {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground shrink-0" />}
            </div>
          </div>

          {/* Chosen first, always visible, so the control never forgets what it is
              filtering by while somebody types a new search. */}
          {selected.length > 0 && (
            <div className="max-h-32 overflow-y-auto border-b border-border pb-1 mb-1">
              {selected.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => toggle(o)}
                  className="w-full px-3 py-1.5 flex items-center gap-2 text-erp-sm hover:bg-muted text-left"
                >
                  <span className="h-3.5 w-3.5 shrink-0 border rounded-[3px] bg-chocolate border-chocolate text-chocolate-foreground inline-flex items-center justify-center">
                    <Check className="h-2.5 w-2.5" />
                  </span>
                  <span className="truncate flex-1">{o.label}</span>
                </button>
              ))}
            </div>
          )}

          <div className="max-h-56 overflow-y-auto">
            {term.trim().length < minChars ? (
              <div className="px-3 py-2 text-erp-xs text-muted-foreground">
                Type at least {minChars} characters.
              </div>
            ) : results.filter((o) => !chosen.has(o.value)).length === 0 && !loading ? (
              <div className="px-3 py-2 text-erp-xs text-muted-foreground">Nothing matches.</div>
            ) : (
              results.filter((o) => !chosen.has(o.value)).map((o) => (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => toggle(o)}
                  className="w-full px-3 py-1.5 flex items-center gap-2 text-erp-sm hover:bg-muted text-left"
                >
                  <span className="h-3.5 w-3.5 shrink-0 border border-input rounded-[3px]" />
                  <span className="truncate flex-1">{o.label}</span>
                  {o.sub && <span className="text-erp-xs text-muted-foreground font-mono">{o.sub}</span>}
                </button>
              ))
            )}
          </div>

          {selected.length > 0 && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="w-full mt-1 px-3 py-1.5 text-erp-xs text-muted-foreground hover:text-foreground border-t border-border text-left"
            >
              Clear {label.toLowerCase()}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Date range. Two native date inputs — a bespoke calendar would be the single
 * largest component in this bundle and native pickers are already localised,
 * keyboard-operable and touch-friendly.
 *
 * Values are yyyy-mm-dd. The caller converts to instants at the Nairobi day
 * boundary (`dayStartIso` / `dayEndIso`), because `to` is EXCLUSIVE server-side.
 */
export function DateRange({
  from, to, onChange, className,
}: {
  from: string
  to: string
  onChange: (next: { from: string; to: string }) => void
  className?: string
}) {
  const active = Boolean(from || to)
  return (
    <div className={cn(
      'h-9 px-2.5 inline-flex items-center gap-1.5 border rounded-md bg-background axis-full-sm',
      active ? 'border-chocolate/50' : 'border-input',
      className,
    )}>
      <DatePicker
        value={from}
        max={to || undefined}
        onChange={(v) => onChange({ from: v, to })}
        aria-label="From date"
        placeholder="From"
        className="border-0 bg-transparent px-0 w-[9.5rem] min-w-0"
      />
      <span className="text-muted-foreground text-erp-xs">→</span>
      <DatePicker
        value={to}
        min={from || undefined}
        onChange={(v) => onChange({ from, to: v })}
        aria-label="To date"
        placeholder="To"
        className="border-0 bg-transparent px-0 w-[9.5rem] min-w-0"
      />
      {active && (
        <button
          type="button"
          onClick={() => onChange({ from: '', to: '' })}
          aria-label="Clear date range"
          className="text-muted-foreground hover:text-foreground shrink-0"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  )
}

/** Summary of what is filtered, with a one-click reset. */
export function ActiveFilters({
  count, onClear,
}: { count: number; onClear: () => void }) {
  if (count === 0) return null
  return (
    <button
      type="button"
      onClick={onClear}
      className="h-9 px-3 inline-flex items-center gap-1.5 text-erp-sm text-chocolate hover:bg-chocolate-soft/40 rounded-md"
    >
      <X className="h-3.5 w-3.5" />
      Clear {count} filter{count === 1 ? '' : 's'}
    </button>
  )
}
